import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import {
  getErrorLoadingSessionPermissions,
  getIsLoadingSessionPermissions,
  getSessionPermissions,
  populateSessionPermissions,
} from "permissions/_stores/permissions-redux-slice";
import useSessionUser from "permissions/session/use-session-user";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router";
import { useSession } from "users/__session/session-context";
import type { User } from "users/user";

type OnboardingRouteState = {
  isLoggedIn: boolean;
  isRestrictedOnboarding: boolean;
  onboardingWork?: {
    state: string;
    currentProposalId?: string | null;
  };
  openEntityAgreementId?: string;
};

type RouteCapability = {
  isPermitted: (user: User) => boolean;
  /** Whether the answer depends on grants, and so cannot be settled before they load. */
  dependsOnGrants: boolean;
};

/**
 * What each guarded route family asks of the signed-in user, in the same words its navigation entry
 * uses. Every entry is a capability question answered from the session's permissions rather than a
 * role test, so a route and the link that leads to it can never disagree (issue #4651); the one
 * route that is genuinely identity — platform administration — says so through the same interface,
 * and is the only one here that can be settled without waiting for a grant to arrive.
 *
 * A route root absent from this table is open to any signed-in session.
 */
const routeCapabilities: Record<string, RouteCapability> = {
  // Reading, not setting. Somebody who may see the terms but not price them reaches the page and
  // is told so there, because the gap it reports is the answer to "why is nothing being built",
  // and that is worth reading whether or not the reader is the person who closes it.
  "client-proposal-templates": {
    isPermitted: (user) => user.canViewClientProposalTemplates(),
    dependsOnGrants: true,
  },
  "firm-onboarding": { isPermitted: (user) => user.canViewFirmOnboardingStatus(), dependsOnGrants: true },
  // Entering a roster is the same authority as entering one person repeatedly, so both rosters
  // are gated on the capability their single-invitation equivalent already asks for.
  "invite-colleagues": { isPermitted: (user) => user.canAccessAdministration(), dependsOnGrants: true },
  "invite-team-leaders": { isPermitted: (user) => user.canAdministerPlatform(), dependsOnGrants: false },
  network: { isPermitted: (user) => user.canAccessNetwork(), dependsOnGrants: true },
  proposals: { isPermitted: (user) => user.canAccessProposals(), dependsOnGrants: true },
  "super-user": { isPermitted: (user) => user.canAdministerPlatform(), dependsOnGrants: false },
  work: { isPermitted: (user) => user.canAccessWork(), dependsOnGrants: true },
};

export function isRouteWithinRoot(pathname: string, routeRoot: string): boolean {
  return pathname === `/${routeRoot}` || pathname.startsWith(`/${routeRoot}/`);
}

function getRouteRoot(pathname: string): string | undefined {
  return pathname.split("/").filter(Boolean)[0];
}

/**
 * Whether reaching a verdict on this route needs the session permissions in hand. The guard holds
 * the render rather than answering from an empty set, which would deny a route the session is in
 * fact entitled to for as long as the request is in flight.
 */
export function routeRequiresSessionPermissions(pathname: string): boolean {
  const routeRoot = getRouteRoot(pathname);
  return routeRoot !== undefined && routeCapabilities[routeRoot]?.dependsOnGrants === true;
}

/**
 * Resolves route access in one place, by asking the user what they may do rather than what they are.
 * An unavailable user answers nothing, so a guarded route fails closed until the session is known.
 *
 * The user must be one that carries its grants — `useSessionUser` — or every grant-dependent route
 * answers false.
 */
export function canUserAccessRoute(pathname: string, user: User | null | undefined): boolean {
  const routeRoot = getRouteRoot(pathname);
  if (!routeRoot) return true;

  const capability = routeCapabilities[routeRoot];
  if (!capability) return true;

  return user ? capability.isPermitted(user) : false;
}

export function getOnboardingRedirect(
  pathname: string,
  session: OnboardingRouteState,
  wasRestricted: boolean,
): string | null {
  if (!session.isLoggedIn) return null;

  const currentProposalId = session.onboardingWork?.currentProposalId;
  const currentProposalPath = session.onboardingWork?.state === "ProposalInProgress" && currentProposalId
    ? `/proposals/active/revise/${currentProposalId}/details`
    : "/proposals";

  if (pathname === "/") {
    if (session.openEntityAgreementId) return `/entity-agreements/${session.openEntityAgreementId}`;
    return session.isRestrictedOnboarding ? currentProposalPath : "/dashboard";
  }

  if (!session.isRestrictedOnboarding) {
    return wasRestricted && session.onboardingWork === undefined ? "/dashboard" : null;
  }

  const segments = pathname.split("/").filter(Boolean);
  const isProposalIndexRoute = segments[0] === "proposals" &&
    (segments.length === 1 || (segments.length === 2 && segments[1] === "active"));
  if (currentProposalId && isProposalIndexRoute) return currentProposalPath;

  const isCurrentProposalRoute = segments[0] === "proposals" &&
    segments[1] === "active" &&
    segments[3] === currentProposalId;
  const isOnboardingRoute = isProposalIndexRoute ||
    isCurrentProposalRoute ||
    segments[0] === "entity-agreements" ||
    (segments.length === 1 && segments[0] === "help");

  return isOnboardingRoute ? null : currentProposalPath;
}

export default function RouteAccessGuard({ children }: { readonly children: React.ReactNode }) {
  const location = useLocation();
  const { pathname } = location;
  const session = useSession();
  const user = useSessionUser();
  const dispatch = useAttorneyHubDispatch();
  const userPermissions = useSelector(getSessionPermissions);
  const isLoadingUserPermissions = useSelector(getIsLoadingSessionPermissions);
  const userPermissionsError = useSelector(getErrorLoadingSessionPermissions);
  const [restrictionTransition, setRestrictionTransition] = useState({
    isRestricted: session.isRestrictedOnboarding,
    promotionPath: null as string | null,
  });

  useEffect(() => {
    if (
      session.isLoggedIn &&
      session.authToken &&
      !userPermissions &&
      !isLoadingUserPermissions &&
      !userPermissionsError
    ) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, isLoadingUserPermissions, session, userPermissions, userPermissionsError]);

  // The application shell belongs to signed-in people only. `RequireSession` guards the routes
  // rendered inside the shell, but nothing guarded the shell itself, so an expired session that
  // opened "/" — the address people actually type — drew a header reading "Viewing as None" over
  // an empty page, with no route matched and no way back to the login form.
  if (!session.isLoggedIn) return <Navigate to="/login" state={{ from: location }} replace />;

  if (restrictionTransition.isRestricted !== session.isRestrictedOnboarding) {
    setRestrictionTransition({
      isRestricted: session.isRestrictedOnboarding,
      promotionPath: restrictionTransition.isRestricted &&
        !session.isRestrictedOnboarding &&
        session.onboardingWork === undefined
        ? pathname
        : null,
    });
    return null;
  }

  if (restrictionTransition.promotionPath && restrictionTransition.promotionPath !== pathname) {
    setRestrictionTransition({
      isRestricted: session.isRestrictedOnboarding,
      promotionPath: null,
    });
    return null;
  }

  const redirectTo = getOnboardingRedirect(pathname, {
    isLoggedIn: session.isLoggedIn,
    isRestrictedOnboarding: session.isRestrictedOnboarding,
    onboardingWork: session.onboardingWork,
    openEntityAgreementId: session.openEntityAgreements[0]?.id.value,
  }, restrictionTransition.promotionPath === pathname);

  if (redirectTo) return <Navigate to={redirectTo} replace />;
  if (routeRequiresSessionPermissions(pathname) && !userPermissions && !userPermissionsError) return null;
  if (!canUserAccessRoute(pathname, user)) return <NotAuthorized />;

  return children;
}
